import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Row, Table } from "react-bootstrap";
import CancelButton from "Common/Components/Buttons/CancelButton";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { fetchInwardFormData } from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk"; 

const ViewReturnedAccessoriesPending: React.FC = () => {
  document.title = "View Pending Returned Accessories";

  const { challan_id } = useParams<{ challan_id: string }>();
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!challan_id) return;
    setLoading(true);
    dispatch(fetchInwardFormData({ challanId: challan_id }))
      .unwrap()
      .then((res: any) => setData(res?.data || res))
      .catch((err: any) => toast.error(err?.message || "Failed to fetch pending returns details"))
      .finally(() => setLoading(false));
  }, [dispatch, challan_id]);

  const dv = (val: any) => (val !== null && val !== undefined && val !== "" ? val : "-");

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title="View Pending Returns"
          titleLink="/warehouse-item-inward"
          breadcrumbs={[
            { label: "Item Inward", to: "/warehouse-item-inward" },
            { label: "View Pending Returns", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Challan Details */}
            <Row className="mb-3 g-3">
              <Col lg={12} md={12}>
                <h5 className="fw-semibold mb-2">Challan Details</h5>
                <div
                  className="p-3 rounded border mb-2"
                  style={{ backgroundColor: MecordColors?.ActiveBackground }}
                >
                  <Row className="g-3">
                    <Col md={3}>
                      <p className="mb-2">
                        <strong>Challan No.:</strong>{" "}
                        {dv(data?.challan_no)}
                      </p>
                    </Col>
                    <Col md={3}>
                      <p className="mb-2">
                        <strong>Challan Date:</strong>{" "}
                        <FormattedDate date={data?.challan_date} />
                      </p>
                    </Col>
                    <Col md={3}>
                      <p className="mb-2">
                        <strong>Sales Order No.:</strong>{" "}
                        {dv(data?.sales_order_number)}
                      </p>
                    </Col>
                    <Col md={3}>
                      <p className="mb-2">
                        <strong>Warehouse:</strong>{" "}
                        {dv(data?.warehouse_name)}
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <hr />

            {/* Products Table (Iterating pending_batches) */}
            <h5 className="fw-semibold mb-2">Pending Accessories</h5>
            <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
              <Table className="table align-middle">
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th style={{ minWidth: 60 }}>Sr. No</th>
                    <th style={{ minWidth: 180 }}>Accessory</th>
                    <th style={{ minWidth: 150 }}>SKU</th>
                    <th style={{ minWidth: 120, textAlign: "center" }}>Returned Qty</th>
                    <th style={{ minWidth: 120, textAlign: "center" }}>Inwarded Qty</th>
                    <th style={{ minWidth: 120, textAlign: "center" }}>Pending Qty</th>
                    <th style={{ minWidth: 160 }}>Service</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows: React.ReactNode[] = [];
                    let sr = 0;
                    const batches = Array.isArray(data?.pending_batches) ? data.pending_batches : [];
                    batches.forEach((batch: any) => {
                      const items: any[] = Array.isArray(batch.items) ? batch.items : [];
                      items.forEach((item: any) => {
                        const accs: any[] = Array.isArray(item.accessories) ? item.accessories : [];
                        accs.forEach((acc: any) => {
                          if (acc.pending_qty > 0) {
                            sr += 1;
                            rows.push(
                              <tr key={`${batch.return_id}-${item.sales_order_service_item_id}-${sr}`}>
                                <td>{sr}</td>
                                <td>{dv(acc.product_name)}</td>
                                <td>{dv(acc.product_sku)}</td>
                                <td style={{ textAlign: "center" }}>{dv(acc.return_qty)}</td>
                                <td style={{ textAlign: "center" }}>{dv(acc.inwarded_qty)}</td>
                                <td style={{ textAlign: "center", fontWeight: "bold" }}>{dv(acc.pending_qty)}</td>
                                <td>{dv(item.product_name)}</td>
                              </tr>
                            );
                          }
                        });
                      });
                    });
                    if (rows.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} className="text-center text-muted">
                            No pending accessories found
                          </td>
                        </tr>
                      );
                    }
                    return rows;
                  })()}
                </tbody>
              </Table>
            </div>

            {/* Actions */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelButton showConfirmation={false} redirectTo="/warehouse-item-inward" />
            </div>
          </>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default ViewReturnedAccessoriesPending;
